import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable, getSelectColumn, SortableHeader } from '@/components/ui/data-table';
import { StatusBadge } from '@/components/ui/status-badge';
import { FloatingActionBar } from '@/components/ui/floating-action-bar';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { creditMemoApi, queryFn, type CreditMemo } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { MoreVertical, FileText, Send, Archive, Trash } from 'lucide-react';

const PAGE_SIZE = 25;

function CreditMemosContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness, isViewOnly } = useApp();
  const queryClient = useQueryClient();
  const [selectedCreditMemos, setSelectedCreditMemos] = useState<CreditMemo[]>([]);

  // Server-side pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const getOrderingParam = () => {
    if (sorting.length === 0) return undefined;
    const { id, desc } = sorting[0];
    return desc ? `-${id}` : id;
  };

  const { data: cmPage, isLoading } = useQuery({
    queryKey: ['credit-memos', currentBusiness?.id, pageIndex, sorting],
    queryFn: () => queryFn(creditMemoApi.list(
      currentBusiness!.id,
      undefined,
      undefined,
      undefined,
      undefined,
      {
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        ordering: getOrderingParam(),
      }
    )),
    enabled: !!currentBusiness,
  });

  const creditMemos = cmPage?.results ?? [];
  const totalCount = cmPage?.count ?? 0;

  const invalidateCMs = () => queryClient.invalidateQueries({ queryKey: ['credit-memos'] });

  const handleDeleteCreditMemo = async (id: string) => {
    if (!confirm('Are you sure you want to delete this credit memo?')) return;
    const response = await creditMemoApi.delete(id);
    if (response.error) {
      alert(response.error);
    } else {
      invalidateCMs();
    }
  };

  // Bulk action handlers
  const handleBulkArchive = async () => {
    for (const creditMemo of selectedCreditMemos) {
      await creditMemoApi.archive(creditMemo.id);
    }
    setSelectedCreditMemos([]);
    invalidateCMs();
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedCreditMemos.length} credit memo(s)?`)) return;
    for (const creditMemo of selectedCreditMemos) {
      await creditMemoApi.delete(creditMemo.id);
    }
    setSelectedCreditMemos([]);
    invalidateCMs();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };


  const columns: ColumnDef<CreditMemo>[] = [
    getSelectColumn<CreditMemo>(),
    {
      accessorKey: 'credit_memo_number',
      header: ({ column }) => <SortableHeader column={column} title="Credit Memo #" />,
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.getValue('credit_memo_number')}</span>
      ),
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.client_name || '-'}</div>
          {row.original.client_company && (
            <div className="text-xs text-muted-foreground truncate">{row.original.client_company}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'credit_memo_date',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDate(row.getValue('credit_memo_date'))}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;

        const config: Record<string, { dot: 'gray' | 'orange' | 'green' | 'red'; label: string }> = {
          draft: { dot: 'gray', label: 'Draft' },
          awaiting_allocation: { dot: 'orange', label: 'Awaiting Allocation' },
          allocated: { dot: 'green', label: 'Allocated' },
          cancelled: { dot: 'red', label: 'Cancelled' },
        };

        const statusConfig = config[status] || { dot: 'gray', label: status };
        return <StatusBadge dot={statusConfig.dot} label={statusConfig.label} />;
      },
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Total" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-sm font-medium">
          {formatAmount(row.getValue('total_amount'))}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCreditMemo(row.original.id);
                }}
                className="text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage>
        <ListPageHeader>
          <ListPageTitle>Credit Memos</ListPageTitle>
          <ListPageActions>
            {!isViewOnly && (
              <Button onClick={() => nav('/credit-memos/create')} className="rounded-full">
                <Send className="h-4 w-4" />
                New Credit Memo
              </Button>
            )}
          </ListPageActions>
        </ListPageHeader>

        <ListPageCard>
          <ListPageContent>
            <DataTable
              columns={columns}
              data={creditMemos}
              loading={isLoading}
              onRowClick={(creditMemo) => nav(`/credit-memos/${creditMemo.id}`)}
              noBorder
              enableRowSelection
              onSelectionChange={setSelectedCreditMemos}
              emptyIcon={<FileText className="h-6 w-6 text-muted-foreground" />}
              emptyTitle="No credit memos yet"
              emptyDescription="Create your first credit memo to get started."
              // Server-side pagination
              manualPagination
              manualSorting
              pageSize={PAGE_SIZE}
              pageIndex={pageIndex}
              totalCount={totalCount}
              onPaginationChange={(newPageIndex) => setPageIndex(newPageIndex)}
              sorting={sorting}
              onSortingChange={(newSorting) => {
                setSorting(newSorting);
                setPageIndex(0); // Reset to first page when sort changes
              }}
            />
          </ListPageContent>
        </ListPageCard>
      </ListPage>

      <FloatingActionBar
        selectedCount={selectedCreditMemos.length}
        onClear={() => setSelectedCreditMemos([])}
      >
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkArchive}
        >
          <Archive className="h-4 w-4 mr-1" /> Archive
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkDelete}
        >
          <Trash className="h-4 w-4 mr-1" /> Delete
        </Button>
      </FloatingActionBar>
    </>
  );
}

export function CreditMemos() {
  return <CreditMemosContent />;
}
